import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const ServiceGallery = ({ items, lang, title }) => {
  const [open, setOpen] = useState(null);

  const close = useCallback(() => setOpen(null), []);
  const prev = useCallback(
    (e) => {
      e?.stopPropagation();
      setOpen((i) => (i === null ? null : (i - 1 + items.length) % items.length));
    },
    [items.length]
  );
  const next = useCallback(
    (e) => {
      e?.stopPropagation();
      setOpen((i) => (i === null ? null : (i + 1) % items.length));
    },
    [items.length]
  );

  useEffect(() => {
    if (open === null) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close, prev, next]);

  // Bento layout: pattern repeats every 5 items (big + 2 small + 2 wide)
  const layoutClass = (i) => {
    const m = i % 5;
    if (m === 0) return "md:col-span-8 md:row-span-2 aspect-[16/10] md:aspect-auto";
    if (m === 1) return "md:col-span-4 aspect-[4/3]";
    if (m === 2) return "md:col-span-4 aspect-[4/3]";
    if (m === 3) return "md:col-span-6 aspect-[16/10]";
    return "md:col-span-6 aspect-[16/10]";
  };

  return (
    <section data-testid="service-gallery" className="py-20 md:py-24 border-t border-[var(--border)]">
      <div className="container-page">
        <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-10">
          <span className="w-8 h-px bg-neutral-400" />
          {title}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          {items.map((p, i) => (
            <button
              key={i}
              type="button"
              data-testid={`gallery-item-${i}`}
              onClick={() => setOpen(i)}
              className={`group relative overflow-hidden bg-neutral-200 text-left ${layoutClass(i)}`}
            >
              <img
                src={p.src}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-3 right-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {open !== null && (
        <div
          data-testid="gallery-lightbox"
          onClick={close}
          className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12 fade-up"
          role="dialog"
          aria-modal="true"
        >
          <button
            data-testid="lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            aria-label="Close"
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
          >
            <X size={28} />
          </button>

          <button
            data-testid="lightbox-prev"
            onClick={prev}
            aria-label="Previous"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ChevronLeft size={36} />
          </button>

          <button
            data-testid="lightbox-next"
            onClick={next}
            aria-label="Next"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ChevronRight size={36} />
          </button>

          <div className="max-w-6xl max-h-full w-full flex flex-col items-center gap-5" onClick={(e) => e.stopPropagation()}>
            <img
              src={items[open].src}
              alt=""
              className="max-h-[85vh] w-auto max-w-full object-contain shadow-2xl"
            />
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
              {String(open + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ServiceGallery;
